import { useCallback, useEffect, useRef } from 'react';

import { notifyIfAccessChanged } from '../utils/apiAuth';

const apiUrl = process.env.REACT_APP_API_URL || '';

export const useApi = (googleIdToken, handleLogout) => {
  // Almost every caller passes an inline arrow (`() => window.location.reload()`),
  // which is a new function on every render. If that landed in apiRequest's
  // dependency list, apiRequest would get a new identity on every render — and
  // any component that put it in a useCallback/useEffect dependency array would
  // re-run its fetch, set state, re-render, and fetch again, forever. So keep
  // the callback in a ref and depend only on the token.
  //
  // The alternative — telling each caller to memoize its handler — is a rule
  // that has to be remembered at nine call sites and will be missed at the
  // tenth. It is only read on a 401, so a ref costs nothing.
  const handleLogoutRef = useRef(handleLogout);
  useEffect(() => {
    handleLogoutRef.current = handleLogout;
  }, [handleLogout]);

  const apiRequest = useCallback(async (path, options = {}) => {
    const url = `${apiUrl}${path}`;

    const headers = {
      'Authorization': `Bearer ${googleIdToken}`,
      ...options.headers,
    };

    // Automatically add Content-Type for requests with a body
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const config = {
      ...options,
      headers,
    };

    try {
      const res = await fetch(url, config);

      if (res.status === 401) {
        // Optional: CustomerSummary calls useApi with no logout handler at all,
        // where this used to throw a TypeError over the real 401.
        handleLogoutRef.current?.();
        const err = new Error('Unauthorized');
        err.status = 401;
        throw err;
      }

      // Try to parse JSON regardless of status, as error responses might have a body.
      const responseData = await res.json().catch(() => ({})); // Gracefully handle non-JSON responses

      if (res.status === 403) {
        notifyIfAccessChanged(responseData);
      }

      if (!res.ok) {
        // `message` first: the route guard puts a machine code in `error` and
        // the sentence in `message`, so using `error` here surfaced raw codes
        // like "insufficient_permissions" in any toast built from
        // error.message. Older handlers send only `error`, holding human text,
        // which this ordering still picks up. errorText() in utils/apiErrors
        // does the same job with translations when a component has `t`.
        const err = new Error(
          responseData.message || responseData.error || `HTTP error ${res.status}`
        );
        err.status = res.status;
        err.code = responseData.error || '';
        err.data = responseData;
        throw err;
      }

      return responseData;
    } catch (error) {
      // Avoid logging errors that are intentionally thrown from above (like HTTP status errors)
      if (!error.status) {
        console.error("API request failed:", error);
      }
      // Re-throw the error to be caught by the calling component
      throw error;
    }
  }, [googleIdToken]);

  return { apiRequest };
};
